import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from 'store/store';
import {useDropdownActionMenu} from 'hooks/useDropdownActionMenu';
import usePermissions from 'hooks/usePermissions';
import TableHeader from 'components/table/TableHeader';
import TableRow from 'components/table/TableRow';
import TableCell from 'components/table/TableCell';
import Table from 'components/table/Table';
import CheckCircleIcon from 'assets/images/icons/CheckCircleIcon';
import XCircleIcon from 'assets/images/icons/XCircleIcon';
import {ProgramCardsThunks} from './programCardThunks';
import {ProgramCard} from 'types/programCard';
import {setActiveTab, PROGRAM_TABS} from 'features/programs/programsSlice';
import {useParams} from 'react-router-dom';
import DotsMenu from '../../../../components/DotsMenu';

const ProgramCardsTable: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {setDotsMenuIsOpen} = useDropdownActionMenu();
  const {checkPermission} = usePermissions();
  const {programId} = useParams<{programId: string}>();
  const programCards = useSelector(
    (state: RootState) => state.programCard.programCards,
  );
  const pagy = useSelector((state: RootState) => state.programCard.pagy);
  const loading = useSelector(
    (state: RootState) => state.programCard.status === 'loading',
  );

  interface VisibleColumnsType {
    name: boolean;
    enable: boolean;
  }

  const [visibleColumns, setVisibleColumns] =
    React.useState<VisibleColumnsType>({
      name: true,
      enable: true,
    });

  const columnOptions = [
    {label: 'Name', value: 'name', isVisible: visibleColumns.name},
    {label: 'Enable', value: 'enable', isVisible: visibleColumns.enable},
  ];

  const searchByOptions = [{label: 'Name', value: 'name', type: 'text'}];

  const fetchData = async ({
    page,
    search,
    sortField,
    sortDirection,
    rowsPerPage,
  }: {
    page: number;
    search: any;
    sortField: string;
    sortDirection: string;
    rowsPerPage: number;
  }): Promise<void> => {
    search.where.program_id = {
      operator: '==',
      value: programId,
    };
    await dispatch(
      ProgramCardsThunks.index({
        page,
        search,
        sortField,
        sortDirection,
        rowsPerPage,
      }),
    );
  };

  const handleColumnToggle = (column: string) => {
    if (column in visibleColumns) {
      setVisibleColumns(prev => ({
        ...prev,
        [column]: !prev[column as keyof VisibleColumnsType],
      }));
    }
  };

  const handleEnable = (programCard: ProgramCard) => {
    const updatedCard = {...programCard, enabled: true};
    dispatch(ProgramCardsThunks.update(updatedCard));
    setDotsMenuIsOpen(null);
  };

  const handleDisable = (programCard: ProgramCard) => {
    const updatedCard = {...programCard, enabled: false};
    dispatch(ProgramCardsThunks.update(updatedCard));
    setDotsMenuIsOpen(null);
  };

  return (
    <Table
      fetchData={fetchData}
      pagy={pagy}
      pageable={true}
      activeTab={'Cards'}
      setActiveTab={(tab: string) => dispatch(setActiveTab(tab))}
      tabs={PROGRAM_TABS}
      recordsName={'Program Cards'}
      loading={loading}
      searchable={true}
      columns={true}
      columnOptions={columnOptions}
      onColumnToggle={handleColumnToggle}
      searchByOptions={searchByOptions}>
      <thead>
        <tr>
          {visibleColumns.name && (
            <TableHeader label="Card" sortBy="card.name" />
          )}
          {visibleColumns.enable && <TableHeader label="Enable" />}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-neutral-700">
        {programCards.map((programCard: ProgramCard) => {
          const userOptions = [];
          if (checkPermission('update', 'Card') && programCard.enabled) {
            userOptions.push({
              label: 'Disable',
              onClick: () => handleDisable(programCard),
              hoverClass: 'hover:bg-cyan-100',
            });
          }
          if (checkPermission('update', 'Card') && !programCard.enabled) {
            userOptions.push({
              label: 'Enable',
              onClick: () => handleEnable(programCard),
              hoverClass: 'hover:bg-cyan-100',
            });
          }
          return (
            <TableRow key={programCard.id}>
              {visibleColumns.name && (
                <TableCell>
                  <span className="text-sm font-medium text-gray-700 dark:text-neutral-300">
                    {programCard.name}
                  </span>
                </TableCell>
              )}
              {visibleColumns.enable && (
                <TableCell>
                  {programCard.enabled ? (
                    <CheckCircleIcon className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <XCircleIcon className="w-5 h-5 text-red-500" />
                  )}
                </TableCell>
              )}
              <TableCell>
                <DotsMenu options={userOptions} />
              </TableCell>
            </TableRow>
          );
        })}
      </tbody>
    </Table>
  );
};

export default ProgramCardsTable;
